// Lista de links da bio: adicionar, editar, ligar/desligar, excluir e reordenar
// arrastando (dnd-kit). Mostra a contagem de cliques de cada link.

import { useEffect, useState, type FormEvent } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, MousePointerClick, Pencil, Plus, Trash2 } from "lucide-react";
import { MAX_BIO_LINKS, MAX_BIO_LINK_TITLE, moveItem, type BioLink } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDeleteBioLink, useReorderBioLinks, useUpsertBioLink } from "@/lib/hooks/useBioPage";
import { cn } from "@/lib/utils";

interface EditorProps {
  readonly links: readonly BioLink[];
  readonly disabled: boolean;
  readonly onMessage: (text: string, ok: boolean) => void;
}

function LinkForm({ initial, onSubmit, submitLabel, onCancel }: {
  initial: { title: string; url: string };
  onSubmit: (title: string, url: string) => Promise<boolean>;
  submitLabel: string;
  onCancel?: () => void;
}): JSX.Element {
  const [title, setTitle] = useState(initial.title);
  const [url, setUrl] = useState(initial.url);
  async function handle(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (await onSubmit(title.trim(), url.trim())) {
      setTitle("");
      setUrl("");
    }
  }
  return (
    <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(e) => void handle(e)}>
      <Input aria-label="Título do link" placeholder="Título (ex.: Grupo VIP)" value={title} maxLength={MAX_BIO_LINK_TITLE} onChange={(e) => setTitle(e.target.value)} />
      <Input aria-label="Endereço do link" type="url" placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} />
      <div className="flex gap-2">
        <Button type="submit" size="sm" className="h-9" disabled={!title.trim() || !url.trim()}>
          <Plus size={13} /> {submitLabel}
        </Button>
        {onCancel && <Button type="button" size="sm" variant="ghost" className="h-9" onClick={onCancel}>Cancelar</Button>}
      </div>
    </form>
  );
}

function SortableLink({ link, disabled, onMessage }: { link: BioLink; disabled: boolean; onMessage: EditorProps["onMessage"] }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });
  const upsert = useUpsertBioLink();
  const remove = useDeleteBioLink();
  const [editing, setEditing] = useState(false);
  const save = async (title: string, url: string, isActive = link.isActive): Promise<boolean> => {
    try {
      await upsert.mutateAsync({ id: link.id, title, url, isActive });
      setEditing(false);
      return true;
    } catch (e) {
      onMessage(e instanceof Error ? e.message : "Erro ao salvar o link.", false);
      return false;
    }
  };
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={cn("rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-2.5", isDragging && "opacity-60 shadow-md", !link.isActive && "opacity-70")}>
      <div className="flex items-center gap-2">
        <button type="button" aria-label={`Reordenar ${link.title}`} className="flex h-8 w-7 shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground" disabled={disabled} {...attributes} {...listeners}>
          <GripVertical size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{link.title}</p>
          <p className="truncate text-xs text-muted-foreground">{link.url}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground" title="Cliques">
          <MousePointerClick size={12} /> {link.clickCount}
        </span>
        <Button size="icon-sm" variant="ghost" disabled={disabled} aria-label={link.isActive ? `Ocultar ${link.title}` : `Mostrar ${link.title}`} onClick={() => void save(link.title, link.url, !link.isActive)}>
          {link.isActive ? <Eye size={13} /> : <EyeOff size={13} />}
        </Button>
        <Button size="icon-sm" variant="ghost" disabled={disabled} aria-label={`Editar ${link.title}`} onClick={() => setEditing((v) => !v)}>
          <Pencil size={13} />
        </Button>
        <Button size="icon-sm" variant="ghost" disabled={disabled || remove.isPending} aria-label={`Excluir ${link.title}`} onClick={() => remove.mutateAsync(link.id).catch((e: unknown) => onMessage(e instanceof Error ? e.message : "Erro ao excluir.", false))}>
          <Trash2 size={13} />
        </Button>
      </div>
      {editing && (
        <div className="mt-2">
          <LinkForm initial={{ title: link.title, url: link.url }} submitLabel="Salvar" onSubmit={(t, u) => save(t, u)} onCancel={() => setEditing(false)} />
        </div>
      )}
    </li>
  );
}

export function BioLinksEditor({ links, disabled, onMessage }: EditorProps): JSX.Element {
  const upsert = useUpsertBioLink();
  const reorder = useReorderBioLinks();
  const [order, setOrder] = useState<BioLink[]>([...links]);
  useEffect(() => setOrder([...links]), [links]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = order.findIndex((l) => l.id === active.id);
    const to = order.findIndex((l) => l.id === over.id);
    const next = moveItem(order, from, to);
    setOrder(next);
    reorder.mutate(next.map((l) => l.id), { onError: (e) => onMessage(e.message, false) });
  }

  async function add(title: string, url: string): Promise<boolean> {
    try {
      await upsert.mutateAsync({ id: null, title, url, isActive: true });
      return true;
    } catch (e) {
      onMessage(e instanceof Error ? e.message : "Erro ao adicionar o link.", false);
      return false;
    }
  }

  return (
    <div className="space-y-3">
      {!disabled && order.length < MAX_BIO_LINKS && <LinkForm initial={{ title: "", url: "" }} submitLabel="Adicionar" onSubmit={add} />}
      <p className="text-xs text-muted-foreground">{order.length}/{MAX_BIO_LINKS} links. Arraste pelo ícone para reordenar.</p>
      {order.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Nenhum link ainda.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={order.map((l) => l.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2">
              {order.map((link) => (
                <SortableLink key={link.id} link={link} disabled={disabled} onMessage={onMessage} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
